import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
    Card,
    CardContent,
    CardHeader,
} from "../../components/ui/card";
import { Loader2, Check, AlertCircle, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { apiClient } from "../../api/client";
import { translateBackendError } from "../../utils/errors";

// ─── Custom 4-digit PIN input (no external library) ──────────────────────────
function PinInput({
    value,
    onChange,
    disabled = false,
    autoFocus = false,
}: {
    value: string;
    onChange: (v: string) => void;
    disabled?: boolean;
    autoFocus?: boolean;
}) {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
        onChange(digits);
    };

    const handleBoxClick = () => {
        inputRef.current?.focus();
    };

    return (
        <div className="flex flex-col items-center gap-3">
            {/* Hidden real input — captures typing */}
            <input
                ref={inputRef}
                type="tel"
                inputMode="numeric"
                maxLength={4}
                value={value}
                onChange={handleChange}
                disabled={disabled}
                autoFocus={autoFocus}
                autoComplete="one-time-code"
                style={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                    width: 1,
                    height: 1,
                }}
            />

            {/* Visual boxes */}
            <div
                className="flex gap-3 cursor-text"
                onClick={handleBoxClick}
            >
                {[0, 1, 2, 3].map((i) => {
                    const filled = value.length > i;
                    const isActive = value.length === i;
                    return (
                        <div
                            key={i}
                            style={{
                                width: 48,
                                height: 56,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 22,
                                fontWeight: 700,
                                backgroundColor: "#ffffff",
                                borderRadius: 8,
                                border: filled
                                    ? "2px solid #3b82f6"
                                    : isActive
                                        ? "2px solid #60a5fa"
                                        : "2px solid #d1d5db",
                                boxShadow: isActive
                                    ? "0 0 0 3px rgba(96,165,250,0.25)"
                                    : "0 1px 2px rgba(0,0,0,0.05)",
                                transition: "border-color 0.15s, box-shadow 0.15s",
                                color: "#111827",
                                userSelect: "none",
                            }}
                        >
                            {filled ? (
                                <span style={{ lineHeight: 1 }}>•</span>
                            ) : isActive ? (
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 2,
                                        height: 24,
                                        backgroundColor: "#3b82f6",
                                        animation: "blink 1s step-start infinite",
                                    }}
                                />
                            ) : null}
                        </div>
                    );
                })}
            </div>

            <style>{`
                @keyframes blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                }
            `}</style>
        </div>
    );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function SetupPin() {
    const { t, i18n } = useTranslation(['auth', 'common']);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    // Support both 'token' (new standard) and 'userId' (legacy)
    const token = searchParams.get("token") || searchParams.get("userId");

    const [isLoading, setIsLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isValid, setIsValid] = useState(false);

    const [pin, setPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    const [isPinEntered, setIsPinEntered] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Validate token on mount
    useEffect(() => {
        const validateLink = async () => {
            if (!token) {
                setError(t('auth:setupPin.invalidToken'));
                setIsValidating(false);
                return;
            }

            try {
                const response = await apiClient.get(`/api/auth/users/validate-setup/${token}`);
                if (response.data.valid) {
                    setIsValid(true);
                } else {
                    setError(response.data.message || t('auth:setupPin.expiredLink'));
                }
            } catch (err: any) {
                const rawMsg = err.response?.data?.message || err.message || t('auth:setupPin.validationError');
                setError(translateBackendError(rawMsg, i18n.language));
            } finally {
                setIsValidating(false);
            }
        };

        validateLink();
    }, [token, t]);

    // Advance to confirm step when 4 digits entered
    useEffect(() => {
        if (pin.length === 4 && !isPinEntered) {
            setIsPinEntered(true);
        }
    }, [pin, isPinEntered]);

    // Clear error when inputs change
    useEffect(() => {
        if (error && (pin || confirmPin)) {
            setError(null);
        }
    }, [pin, confirmPin, error]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (pin.length !== 4) {
            setError(t('auth:signup.validation.pin4Digits'));
            return;
        }

        if (pin !== confirmPin) {
            setError(t('auth:signup.validation.pinMismatch'));
            setConfirmPin("");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await apiClient.post("/api/auth/users/setup-pin", {
                token: token,
                pin: pin,
            });

            setIsSuccess(true);
            toast({
                title: t('auth:setupPin.successTitle'),
                description: t('auth:setupPin.successMessage') + " " + t('auth:resetPin.redirecting'),
                type: "success",
            });

            setTimeout(() => {
                navigate("/login", {
                    state: { message: t('auth:setupPin.successTitle') }
                });
            }, 2000);
        } catch (err: any) {
            const rawMsg = err.response?.data?.message || err.message || t('auth:setupPin.setupError');
            setError(translateBackendError(rawMsg, i18n.language));
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setPin("");
        setConfirmPin("");
        setIsPinEntered(false);
    };

    // ── Loading state ──────────────────────────────────────────────────────────
    if (isValidating) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
                <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md">
                    <CardContent className="pt-8 pb-8 text-center">
                        <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
                        <p className="text-gray-600">{t('auth:setupPin.verifyingLink')}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // ── Error state (invalid/expired link) ────────────────────────────────────
    if (!isValid && error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-100 to-gray-100 p-4">
                <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md">
                    <CardHeader className="text-center pt-8">
                        <div className="bg-red-100 p-4 rounded-full w-fit mx-auto mb-4">
                            <AlertCircle className="h-10 w-10 text-red-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900">{t('auth:setupPin.invalidLink')}</h1>
                    </CardHeader>
                    <CardContent className="text-center pb-8">
                        <p className="text-gray-600 mb-6">{error}</p>
                        <Button onClick={() => navigate("/login")} className="w-full">
                            {t('auth:login.loginButton')}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // ── Success state ──────────────────────────────────────────────────────────
    if (isSuccess) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-100 to-gray-100 p-4">
                <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md">
                    <CardHeader className="text-center pt-8">
                        <div className="bg-green-100 p-4 rounded-full w-fit mx-auto mb-4">
                            <ShieldCheck className="h-10 w-10 text-green-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900">{t('auth:setupPin.successTitle')}</h1>
                    </CardHeader>
                    <CardContent className="text-center pb-8">
                        <p className="text-gray-600 mb-4">{t('auth:setupPin.successMessage')}</p>
                        <div className="flex items-center justify-center gap-2 text-green-600">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>{t('auth:resetPin.redirecting')}</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // ── Main PIN setup form ────────────────────────────────────────────────────
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4 sm:p-8">
            <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md overflow-hidden">
                <CardHeader className="flex flex-col items-center text-center space-y-4 pt-8">
                    <div className="bg-primary-100 p-3 rounded-full">
                        <KeyRound className="h-8 w-8 text-primary-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">{t('auth:setupPin.title')}</h1>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('auth:setupPin.subtitle')}
                        </p>
                    </div>
                </CardHeader>

                <CardContent className="pt-4 pb-8">
                    {error && (
                        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                            <AlertCircle size={18} />
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Step 1 — Enter PIN */}
                        {!isPinEntered && (
                            <div className="flex flex-col items-center space-y-4">
                                <Label className="text-gray-700 font-semibold">
                                    {t('auth:setupPin.enterPinLabel')}
                                </Label>
                                <PinInput
                                    value={pin}
                                    onChange={setPin}
                                    disabled={isLoading}
                                    autoFocus={true}
                                />
                            </div>
                        )}

                        {/* Step 2 — Confirm PIN */}
                        {isPinEntered && (
                            <div className="flex flex-col items-center space-y-4">
                                <Label className="text-gray-700 font-semibold">
                                    {t('auth:setupPin.confirmPinLabel')}
                                </Label>
                                <PinInput
                                    value={confirmPin}
                                    onChange={setConfirmPin}
                                    disabled={isLoading}
                                    autoFocus={true}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={handleReset}
                                    disabled={isLoading}
                                    className="text-sm text-gray-500"
                                >
                                    {t('auth:setupPin.changePin')}
                                </Button>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="mt-8">
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={isLoading || pin.length !== 4 || confirmPin.length !== 4}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('auth:setupPin.settingUp')}
                                    </>
                                ) : (
                                    <>
                                        <Check className="mr-2 h-4 w-4" />
                                        {t('auth:setupPin.setupButton')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>

                    {/* Security info */}
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                            {t('auth:setupPin.securityTip')}
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
